import { ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { darken } from 'polished';

import theme from '@styles/theme';

import { FlexBox, Description } from '@components/styled/StyledComponents';

type InfoProps = {
    DETAIL?: boolean;
    children?: ReactNode;
    title: string;
    value?: string;
    description?: string;
    titleColor?: string;
    fontColor?: string;
    fontFamily?: string;
    fontSize?: number;
    buttonTitle?: string;
    onButtonClick?: () => void;
    margin?: string;
    width?: string;
};

type InfoSectionProps = {
    DETAIL?: boolean;
    ROW?: boolean;
    title?: string;
    subTitle?: string;
    margin?: string;
    fontSize?: number;
    fontFamily?: string;
    children: ReactNode;
};

type InfoHistoryProps = {
    histories: string[] | [];
    buttonTitle?: string;
    buttonAction?: () => void;
};

export const InfoSection = ({
    DETAIL,
    ROW,
    title,
    subTitle,
    margin = '0 0 32px',
    // fontFamily = 'PretendardBold',
    fontSize = 16,
    children
}: InfoSectionProps) => {
    return (
        <Wrapper>
            <FlexBox
                $flexDirection={ROW ? 'row' : 'column'}
                $alignItems="flex-start"
            >
                {title && (
                    <InfoTitle
                        $margin={margin}
                        // $fontFamily={fontFamily}
                        $fontSize={fontSize}
                        $detail={DETAIL}
                        $row={ROW}
                    >
                        {title}
                        {subTitle && <SubTitle>{subTitle}</SubTitle>}
                    </InfoTitle>
                )}
                <FlexBox $flexDirection="column" $alignItems="flex-start">
                    {children}
                </FlexBox>
            </FlexBox>
        </Wrapper>
    );
};

export const InfoDataRow = ({
    DETAIL,
    children,
    title,
    value,
    description = '',
    buttonTitle = '',
    titleColor,
    fontColor,
    fontFamily,
    fontSize = 15,
    onButtonClick = () => null,
    margin,
    width
}: InfoProps) => {
    return (
        <InfoRow $margin={margin} $width={width}>
            <InfoTitle
                $row={true}
                $fontColor={titleColor}
                $fontSize={fontSize}
                $detail={DETAIL}
            >
                {title}
            </InfoTitle>
            <FlexBox $flexDirection="column" $alignItems="flex-start">
                <FlexBox $justifyContent="space-between">
                    <InfoValue
                        $fontSize={fontSize}
                        $fontColor={fontColor}
                        $fontFamily={fontFamily}
                    >
                        {children ? children : value}
                    </InfoValue>
                    {buttonTitle && (
                        <Button onClick={onButtonClick}>{buttonTitle}</Button>
                    )}
                </FlexBox>
                {description && <Description>{description}</Description>}
            </FlexBox>
        </InfoRow>
    );
};

export const InfoHistory = ({
    histories,
    buttonTitle,
    buttonAction
}: InfoHistoryProps) => {
    if (histories.length === 0) {
        return <EmptyBox>히스토리가 없습니다.</EmptyBox>;
    }

    return (
        <>
            {histories.map((history, index) => {
                if (!history) return null;

                const title = history?.split('&#58;')?.[0]?.trim();
                const value = history?.split('&#58;')?.[1]?.trim();

                return (
                    <InfoDataRow
                        key={`${index}-history-list`}
                        title={title}
                        value={value}
                        buttonTitle={index === 0 ? buttonTitle : undefined}
                        onButtonClick={index === 0 ? buttonAction : () => null}
                        margin="0 0 20px"
                    />
                );
            })}
        </>
    );
};

const Wrapper = styled.section`
    padding: 32px 0;
    width: 100%;
    border-top: ${theme.colors.ultraLightGrayBorder} 1px solid;
`;
export const InfoWrapper = styled.div<{ $margin?: string; $width?: string }>`
    margin: ${({ $margin }) => ($margin ? $margin : 0)};
    width: ${({ $width }) => ($width ? $width : '100%')};
`;
export const InfoRow = styled.div<{ $margin?: string; $width?: string }>`
    display: flex;
    align-items: flex-start;
    margin: ${({ $margin }) => ($margin ? $margin : '0 0 16px')};
    width: ${({ $width }) => ($width ? $width : '100%')};
`;

export const InfoTitle = styled.h3<{
    $row?: boolean;
    $margin?: string;
    $fontColor?: string;
    $fontSize?: number;
    // $fontFamily?: string;
    $detail?: boolean;
}>`
    display: flex;
    align-items: center;
    margin: ${({ $margin }) => $margin};
    /* font-size: 16px; */
    font-size: ${({ $fontSize }) => $fontSize}px;
    color: ${({ $fontColor }) =>
        $fontColor ? $fontColor : theme.colors.blackColor};
    font-family: PretendardBold, sans-serif;

    ${({ $row, $detail, $fontSize }) =>
        $row &&
        css`
            margin: 0;
            font-size: ${$fontSize}px;
            font-family: ${$detail ? 'PretendardRegular' : 'PretendardMedium'},
                sans-serif;
            padding-right: 20px;
            width: 162px;
        `}
`;

export const SubTitle = styled.span`
    margin-left: 12px;
    font-size: 14px;
    font-family: PretendardMedium, sans-serif;
    color: ${theme.colors.deepGrayFontColor};
`;
export const InfoValue = styled.div<{
    $fontSize?: number;
    $fontColor?: string;
    $fontFamily?: string;
}>`
    max-width: 630px;
    font-size: ${({ $fontSize }) => $fontSize}px;
    white-space: pre-line;
    color: ${({ $fontColor }) =>
        $fontColor ? $fontColor : theme.colors.blackColor};
    font-family: ${({ $fontFamily }) =>
            $fontFamily ? $fontFamily : 'PretendardRegular'},
        sans-serif;
`;

export const Button = styled.button`
    display: flex;
    align-items: center;
    font-size: 14px;
    color: ${theme.colors.deepGrayFontColor};
    transition: all.25s;
    &:hover {
        color: ${darken(0.35, theme.colors.deepGrayFontColor)};
    }
`;
const EmptyBox = styled.div`
    width: 100%;
    height: 100px;
    color: ${theme.colors.deepGrayFontColor};
    display: flex;
    align-items: center;
    justify-content: center;
`;
